import React from 'react';
import PageLayout from '../components/layout/PageLayout';

const AdminGuidePage = () => {
  return (
    <PageLayout headerTitle="Admin-guide" maxWidth="max-w-5xl">
      <div className="space-y-8 pb-8">
        <div className="rounded-2xl border border-cyan-500/40 bg-slate-900/60 p-8">
          <h1 className="text-4xl font-bold text-white mb-2">Quizter – Admin‑guide</h1>
          <p className="text-slate-400 text-sm">Bruksanvisning för superuser och admin‑flöden.</p>
        </div>

        <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
          <h2 className="text-2xl font-bold text-white mb-4 pb-3 border-b border-slate-700">1. Översikt</h2>
          <p className="text-slate-300 mb-4">Admin‑delen används för att styra AI‑generering, validering, regler, betalningar och innehåll.</p>
          <div className="bg-cyan-500/10 border border-cyan-500/40 rounded-lg p-4 mb-4">
            <p className="text-cyan-300">
              <strong className="text-cyan-400">Snabbt:</strong> Gå till Admin Dashboard och välj ett område i menyn.
            </p>
          </div>
          <div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4">
            <h3 className="text-lg font-semibold text-white mb-3">Snabbflöde (rekommenderat)</h3>
            <ol className="space-y-2 text-slate-300 list-decimal list-inside">
              <li>Konfigurera <strong className="text-cyan-400">AI‑providers</strong> och lägg in API‑nycklar.</li>
              <li>Justera <strong className="text-cyan-400">AI‑regler</strong> och <strong className="text-cyan-400">promptmallar</strong>.</li>
              <li>Generera frågor via <strong className="text-cyan-400">Frågebank</strong> eller <strong className="text-cyan-400">AI‑scheman</strong>.</li>
              <li>Följ jobben i <strong className="text-cyan-400">Bakgrundsjobb</strong> och kontrollera loggar.</li>
            </ol>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 pb-3 border-b border-slate-700">2. AI‑flöde & frågebank</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">2.1 Dashboard</h3>
              <figure>
                <img src="/manual-images/admin-dashboard.png" alt="Admin dashboard" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Dashboard med genvägar och status.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">2.2 Frågebank & AI‑generering</h3>
              <ul className="space-y-2 mb-4 text-slate-300">
                <li className="flex items-start"><span className="text-emerald-400 mr-2">•</span>Skapa nya frågor via AI och följ jobben i bakgrundslistan.</li>
                <li className="flex items-start"><span className="text-emerald-400 mr-2">•</span>Filtrera på kategori, ålder, målgrupp, valideringsstatus och karantän.</li>
                <li className="flex items-start"><span className="text-emerald-400 mr-2">•</span>Validera, korrigera och godkänn manuellt vid behov.</li>
              </ul>
              <figure>
                <img src="/manual-images/admin-questions.png" alt="Frågebank" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Filtrera, granska och generera frågor.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">2.3 Användare & rundor</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <figure>
                  <img src="/manual-images/admin-users.png" alt="Användare" className="w-full rounded-lg border border-slate-600" />
                  <figcaption className="text-sm text-slate-400 mt-3">Alla användare med status.</figcaption>
                </figure>
                <figure>
                  <img src="/manual-images/admin-all-runs.png" alt="Alla rundor" className="w-full rounded-lg border border-slate-600" />
                  <figcaption className="text-sm text-slate-400 mt-3">Alla rundor och deras status.</figcaption>
                </figure>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">2.4 Meddelanden & notiser</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <figure>
                  <img src="/manual-images/admin-messages.png" alt="Meddelanden" className="w-full rounded-lg border border-slate-600" />
                  <figcaption className="text-sm text-slate-400 mt-3">Skicka meddelanden.</figcaption>
                </figure>
                <figure>
                  <img src="/manual-images/admin-notifications.png" alt="Notiser" className="w-full rounded-lg border border-slate-600" />
                  <figcaption className="text-sm text-slate-400 mt-3">Notiser och händelser.</figcaption>
                </figure>
                <figure>
                  <img src="/manual-images/admin-logs.png" alt="Loggar" className="w-full rounded-lg border border-slate-600" />
                  <figcaption className="text-sm text-slate-400 mt-3">System‑loggar.</figcaption>
                </figure>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">2.5 Analys</h3>
              <figure>
                <img src="/manual-images/admin-analytics.png" alt="Analys" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Statistik och användningsdata.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">2.6 AI‑providers</h3>
              <figure>
                <img src="/manual-images/admin-ai-providers.png" alt="AI providers" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Aktivera och testa providers.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-emerald-400 mb-3">2.7 AI‑regler</h3>
              <figure>
                <img src="/manual-images/admin-ai-rules.png" alt="AI regler" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Regler per åldersgrupp.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 pb-3 border-b border-slate-700">3. Promptar & innehåll</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-400 mb-3">3.1 Promptmallar</h3>
              <figure>
                <img src="/manual-images/admin-prompts.png" alt="Promptmallar" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Global mall och varianter.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-400 mb-3">3.2 Kategorier</h3>
              <figure>
                <img src="/manual-images/admin-categories.png" alt="Kategorier" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Kategorier och beskrivningar.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-400 mb-3">3.3 Ålders‑ & målgrupper</h3>
              <figure>
                <img src="/manual-images/admin-audiences.png" alt="Målgrupper" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Åldersgrupper och målgrupper.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-400 mb-3">3.4 Moderering</h3>
              <figure>
                <img src="/manual-images/admin-moderation.png" alt="Moderering" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Modereringskö.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 pb-3 border-b border-slate-700">4. Drift & scheman</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-cyan-400 mb-3">4.1 AI‑scheman</h3>
              <figure>
                <img src="/manual-images/admin-ai-schedules.png" alt="AI-scheman" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Schemalägg genereringar.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-cyan-400 mb-3">4.2 Bakgrundsjobb</h3>
              <figure>
                <img src="/manual-images/admin-tasks.png" alt="Bakgrundsjobb" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Övervaka genereringar.</figcaption>
              </figure>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-cyan-400 mb-3">4.3 Provider‑loggar</h3>
              <figure>
                <img src="/manual-images/admin-provider-logs.png" alt="Provider-loggar" className="w-full rounded-lg border border-slate-600" />
                <figcaption className="text-sm text-slate-400 mt-3">Detaljerade provider‑loggar.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
          <h2 className="text-2xl font-bold text-white mb-4 pb-3 border-b border-slate-700">5. Betalningar</h2>
          <figure>
            <img src="/manual-images/admin-payments.png" alt="Betalningar" className="w-full rounded-lg border border-slate-600" />
            <figcaption className="text-sm text-slate-400 mt-3">Betalningsinställningar.</figcaption>
          </figure>
        </section>

        <section className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
          <h2 className="text-2xl font-bold text-white mb-4 pb-3 border-b border-slate-700">6. E‑post & audit</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <figure>
              <img src="/manual-images/admin-email-settings.png" alt="E-post" className="w-full rounded-lg border border-slate-600" />
              <figcaption className="text-sm text-slate-400 mt-3">E‑postinställningar.</figcaption>
            </figure>
            <figure>
              <img src="/manual-images/admin-email-logs.png" alt="E-postloggar" className="w-full rounded-lg border border-slate-600" />
              <figcaption className="text-sm text-slate-400 mt-3">Skickade e‑post.</figcaption>
            </figure>
            <figure>
              <img src="/manual-images/admin-audit-logs.png" alt="Audit" className="w-full rounded-lg border border-slate-600" />
              <figcaption className="text-sm text-slate-400 mt-3">Ändringshistorik.</figcaption>
            </figure>
          </div>
        </section>

        <section className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6">
          <h2 className="text-2xl font-bold text-red-400 mb-4 pb-3 border-b border-red-500/20">7. Tips & felsökning</h2>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start">
              <span className="text-red-400 mr-2">⚠️</span>
              <div><strong className="text-red-300">Validering körs inte:</strong> Se att minst en alternativ provider är aktiv.</div>
            </li>
            <li className="flex items-start">
              <span className="text-red-400 mr-2">⚠️</span>
              <div><strong className="text-red-300">Schema körs inte:</strong> Kontrollera Cron Trigger och APP_BASE_URL.</div>
            </li>
            <li className="flex items-start">
              <span className="text-red-400 mr-2">⚠️</span>
              <div><strong className="text-red-300">Provider fel:</strong> Se provider‑loggar och status.</div>
            </li>
          </ul>
        </section>

        <p className="text-center text-slate-500 text-sm">Dokumentet är genererat internt för Quizter.</p>
      </div>
    </PageLayout>
  );
};

export default AdminGuidePage;
